import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as DownloadIcon } from 'src/assets/tailwind/download-icon.svg';
import { ReactComponent as HelpCircleIcon } from 'src/assets/tailwind/help-circle.svg';
import { ReactComponent as XCloseIcon } from 'src/assets/tailwind/x-close.svg';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter
} from 'src/components/ui/dialog';
import { FileComparisonStatus } from 'src/utils/files/fileComparison';
import { Icon } from 'src/components/ui/icon';
import { Typography } from 'src/components/ui/typography';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

import type { FileComparisonDialogProps } from './types';


export const FileComparisonDialog: React.FC<FileComparisonDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  comparison,
  destinationPath,
  overwrite = false,
  onOverwriteChange,
  isLoading
}) => {
  const { t } = useTranslation();

  const uploadableFiles = useMemo(
    () =>
      overwrite
        ? comparison.files
        : comparison.files.filter((f) => f.status !== FileComparisonStatus.UNCHANGED),
    [comparison.files, overwrite]
  );

  const handleConfirm = () => {
    onConfirm(uploadableFiles.map((f) => f.file));
  };

  const handleDownloadCsv = useCallback(() => {
    const formatDate = (iso: string) => {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      const day = d.getDate();
      const month = d.toLocaleString('en-US', { month: 'short' });
      const year = d.getFullYear();
      return `${day} ${month}-${year}`;
    };
    const basePath = destinationPath
      ? destinationPath.replace(/\/+$/, '')
      : '';
    const header = [
      t(T.csvFileName),
      t(T.csvFullPath),
      t(T.csvSizeBytes),
      t(T.csvLocalModified),
      t(T.csvServerModified),
      t(T.csvStatus)
    ].join(',');
    const rows = comparison.files
      .filter((f) => !(f.file.size === 0 && f.file.type === ''))
      .map((f) => {
        const name = f.file.name.replace(/"/g, '""');
        const fullPath = basePath
          ? `${basePath}/${f.relativePath}`.replace(/"/g, '""')
          : f.relativePath.replace(/"/g, '""');
        const localMod = formatDate(f.localModifiedOn);
        const serverMod = f.serverModifiedOn
          ? formatDate(f.serverModifiedOn)
          : '';
        return `"${name}","${fullPath}",${f.file.size},"${localMod}","${serverMod}","${f.status}"`;
      });
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'upload_files_detail.csv';
    link.click();
    URL.revokeObjectURL(url);
  }, [comparison.files, destinationPath]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        hideDefaultClose
        className="tw:max-w-[680px] tw:p-2xl tw:rounded-3xl tw:gap-0"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="tw:flex tw:flex-col tw:gap-2xs tw:px-2xl tw:py-s">
          <Typography variant="h3" colorClass="tw:text-text-primary">
            {t(T.uploadDetail)}
          </Typography>
          {destinationPath && (
            <Typography variant="b2" colorClass="tw:text-text-secondary">
              {t(T.syncingFilesTo, { path: destinationPath })}
            </Typography>
          )}
        </div>

        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="tw:absolute tw:top-l tw:right-l tw:p-s tw:rounded-full tw:cursor-pointer tw:border-none tw:bg-transparent hover:tw:bg-secondary"
        >
          <Icon
            icon={XCloseIcon}
            sizeClass="tw:h-m tw:w-m"
            colorClass="tw:text-icon-primary"
          />
        </button>

        {/* Body */}
        <div className="tw:flex tw:flex-col tw:gap-l tw:p-2xl">
          {/* Stats bar */}
          <div className="tw:flex tw:items-center tw:justify-between tw:border tw:border-solid tw:border-border-secondary tw:rounded-lg tw:p-l">
            <div className="tw:flex-1 tw:flex tw:flex-col tw:gap-2xs tw:items-start">
              <Typography variant="h6" colorClass="tw:text-text-secondary">
                {t(T.totalFiles)}
              </Typography>
              <Typography variant="h6" colorClass="tw:text-text-primary">
                {comparison.totalCount}
              </Typography>
            </div>
            <div className="tw:flex-1 tw:flex tw:flex-col tw:gap-2xs tw:items-start">
              <Typography variant="h6" colorClass="tw:text-text-secondary">
                {t(T.existingFiles)}
              </Typography>
              <Typography variant="h6" colorClass="tw:text-text-primary">
                {comparison.existingCount}
              </Typography>
            </div>
            <div className="tw:flex-1 tw:flex tw:flex-col tw:gap-2xs tw:items-start">
              <Typography variant="h6" colorClass="tw:text-text-secondary">
                {t(T.newFiles)}
              </Typography>
              <Typography variant="h6" colorClass="tw:text-text-primary">
                {comparison.newCount}
              </Typography>
            </div>
            <Button
              leftIcon={DownloadIcon}
              size="small"
              hierarchy="secondary"
              text={t(T.downloadFilesDetail)}
              onClick={handleDownloadCsv}
            />
          </div>

          {/* Warning alert for existing files */}
          {comparison.existingCount > 0 && (
            <div className="tw:flex tw:gap-l tw:p-l tw:bg-warning tw:border tw:border-solid tw:border-border-warning tw:rounded-bw-2xl-3xl tw:shadow-e1">
              <Icon
                icon={HelpCircleIcon}
                sizeClass="tw:h-xl tw:w-xl"
                colorClass="tw:text-icon-warning"
                className="tw:shrink-0"
              />
              <div className="tw:flex tw:flex-col tw:gap-m tw:flex-1">
                <Typography variant="h4" colorClass="tw:text-text-primary">
                  {t(T.filesAlreadyExist, {
                    count: comparison.existingCount
                  })}
                </Typography>
                <div className="tw:flex tw:items-center tw:gap-m">
                  <Checkbox
                    checked={overwrite}
                    onCheckedChange={(checked) =>
                      onOverwriteChange?.(checked === true)
                    }
                  />
                  <button
                    type="button"
                    onClick={() => onOverwriteChange?.(!overwrite)}
                    className="tw:bg-transparent tw:border-none tw:p-none tw:cursor-pointer"
                  >
                    <Typography
                      variant="h5"
                      colorClass="tw:text-text-link"
                    >
                      {t(T.replaceExistingFiles)}
                    </Typography>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="tw:w-full">
          <div className="tw:flex tw:justify-between tw:items-center tw:w-full tw:px-2xl tw:py-m">
            <Button
              hierarchy="destructive"
              size="small"
              text={t(T.cancel)}
              onClick={onClose}
            />
            <Button
              hierarchy="primary"
              size="small"
              text={t(T.upload)}
              onClick={handleConfirm}
              disabled={uploadableFiles.length === 0}
              loading={isLoading}
            />
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
